import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Training-video ingest schema.
 *
 * Firestore path: `tenants/{tenantId}/videos/{videoId}` for the per-video
 * record. Transcript chunks land in the existing `kennisbank_ring1` /
 * `kennisbank_ring0` collections with the extra `sourceType:'video'` +
 * `videoId` + `startSeconds` + `endSeconds` + `chapterIndex` fields so the
 * citation card can deep-link the player.
 *
 * Single-shared-corpus rule still applies: ring1 videos write chunks
 * with `tenantId: '_platform'` to match the PDF corpus behaviour. The
 * per-video record DOES live under the uploading tenant's subtree (the
 * upload action is tenant-scoped audit data); the chunks themselves are
 * shared. This mirrors the documented exception list in CLAUDE.md.
 */
export const VideoStatus = z.enum([
    'pending_upload',
    'transcoding',
    'transcribing',
    'summarising',
    'indexed',
    'failed',
]);
export const VideoLanguage = z.enum(['en', 'nl', 'mixed']);
export const Video = z.object({
    id: DocId,
    tenantId: z.union([TenantId, z.literal('_platform')]),
    title: z.string().min(1).max(500),
    originalFilename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(120),
    durationSeconds: z.number().nonnegative().optional(),
    sizeBytes: z.number().int().nonnegative(),
    status: VideoStatus,
    ring: z.enum(['ring0', 'ring1']).default('ring1'),
    uploadedBy: DocId,
    uploadedAt: IsoTimestamp,
    transcribedAt: IsoTimestamp.optional(),
    indexedAt: IsoTimestamp.optional(),
    language: VideoLanguage.default('mixed'),
    errorMessage: z.string().max(2000).optional(),
    /** Chapter count produced by the ingest pipeline. */
    chapterCount: z.number().int().nonnegative().optional(),
    /** Tombstone for soft-delete; chunks are marked inactive but GCS file kept. */
    deletedAt: IsoTimestamp.optional(),
    /**
     * Whether the source video had an audio track. Set by the ingest pipeline:
     * false for silent screencasts (visual-only indexing path, no transcript);
     * true otherwise. Optional for backwards compatibility with pre-existing
     * Video docs ingested before this field was introduced.
     */
    audioPresent: z.boolean().optional(),
    /** Optional list of per-chapter summaries surfaced in the player UI. */
    chapters: z
        .array(z.object({
        index: z.number().int().nonnegative(),
        title: z.string().max(200),
        startSeconds: z.number().nonnegative(),
        endSeconds: z.number().nonnegative(),
        visualSummary: z.string().max(2000).optional(),
        keyframeGcsPath: z.string().max(500).optional(),
    }))
        .optional(),
    /**
     * Library metadata — set after ingest by a kb_admin via the metadata
     * editor. All optional and additive so pre-existing Video docs keep
     * validating.
     */
    seriesId: DocId.optional(),
    episode: z.number().int().positive().optional(),
    totalEpisodes: z.number().int().positive().optional(),
    isFeatured: z.boolean().optional(),
    presenter: z
        .object({
        name: z.string().min(1).max(120),
        role: z.string().min(1).max(120).optional(),
        userId: DocId.optional(),
    })
        .optional(),
    resources: z
        .array(z
        .object({
        kind: z.enum(['doc', 'link']),
        title: z.string().min(1).max(200),
        // For kind:'doc' — points at an existing kennisbank ingestedFile
        documentId: z.string().min(1).max(200).optional(),
        // For kind:'link' — external URL or internal route
        href: z.string().url().or(z.string().startsWith('/')).optional(),
    })
        .superRefine((r, ctx) => {
        if (r.kind === 'doc' && !r.documentId) {
            ctx.addIssue({
                code: 'custom',
                message: 'documentId required for doc resources',
            });
        }
        if (r.kind === 'link' && !r.href) {
            ctx.addIssue({ code: 'custom', message: 'href required for link resources' });
        }
    }))
        .max(20)
        .optional(),
    viewCount: z.number().int().nonnegative().optional(),
    /** Operator-set publish date (distinct from uploadedAt which is when the file landed in GCS). */
    publishedAt: IsoTimestamp.optional(),
});
/**
 * Upload-init body. Multer multipart handles the file bytes; this is the
 * JSON metadata posted alongside (or pre-flight before mint upload-url).
 */
export const CreateVideoBody = z.object({
    title: z.string().min(1).max(500),
    originalFilename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(120),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(2 * 1024 * 1024 * 1024), // 2 GB cap
    ring: z.enum(['ring0', 'ring1']).default('ring1'),
    language: VideoLanguage.optional(),
});
export const VIDEO_ALLOWED_MIME_TYPES = new Set([
    'video/mp4',
    'video/quicktime', // .mov
    'video/webm',
    'video/x-matroska', // .mkv
]);
export const VIDEO_MIME_TO_EXT = {
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
    'video/x-matroska': 'mkv',
};
/**
 * Body for PATCH /videos/:id/metadata — kb_admin sets library-facing fields
 * after ingest. Every field is optional; only the keys present are merged.
 *
 * Clearable fields (seriesId, episode, totalEpisodes, presenter, publishedAt)
 * accept `null` to explicitly clear a previously-set value. The route handler
 * translates incoming `null` to `FieldValue.delete()` so the field is removed
 * from the doc rather than stored as a literal null. `title` and `isFeatured`
 * are not nullable — clearing them has no meaningful semantics.
 */
export const UpdateVideoMetadataBody = z.object({
    title: z.string().min(1).max(500).optional(),
    seriesId: DocId.nullable().optional(),
    episode: z.number().int().positive().nullable().optional(),
    totalEpisodes: z.number().int().positive().nullable().optional(),
    isFeatured: z.boolean().optional(),
    presenter: z
        .object({
        name: z.string().min(1).max(120),
        role: z.string().min(1).max(120).optional(),
        userId: DocId.optional(),
    })
        .nullable()
        .optional(),
    resources: z
        .array(z
        .object({
        kind: z.enum(['doc', 'link']),
        title: z.string().min(1).max(200),
        documentId: z.string().min(1).max(200).optional(),
        href: z.string().url().or(z.string().startsWith('/')).optional(),
    })
        .superRefine((r, ctx) => {
        if (r.kind === 'doc' && !r.documentId) {
            ctx.addIssue({
                code: 'custom',
                message: 'documentId required for doc resources',
            });
        }
        if (r.kind === 'link' && !r.href) {
            ctx.addIssue({ code: 'custom', message: 'href required for link resources' });
        }
    }))
        .max(20)
        .optional(),
    publishedAt: IsoTimestamp.nullable().optional(),
});
//# sourceMappingURL=video.js.map